import getRemoteMetricKey from 'common/src/modules/analytics/utils/getRemoteMetricKey';
import {AvailableDimension} from 'common/src/modules/campaigns/store/insightsPage/insightsPage.types';
import {isoDateUtcSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import _find from 'lodash/find';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import {z} from 'zod';
import analyticsApi from '../../../../analytics.api';
import {ANALYTICS_LIMIT} from '../../../../analytics.constants';
import {
  AiAnalyticsEventType,
  AnalyticsEventType,
  AnalyticsFilterItem,
  DimensionValue,
  MetricsGroupBy,
  MetricValue,
} from '../../../../analytics.types';
import getDimensionMainParameter from '../../../../utils/getDimensionMainParameter';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import isUniqueDimensionValue from '../../../../utils/isUniqueDimensionValue';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import partitionByEventType from '../utils/partitionByEventType';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import persistInlineWidgetToAccountDashboard from '../utils/persistInlineWidgetToAccountDashboard';
import {AiDashboardWidgetType} from 'common/src/modules/dashboards/dashboards.types';

const inputSchema = z.object({
  dimension: z
    .string()
    .default(DimensionValue.Date)
    .describe(
      'The viewing dimension to group the data by. When omitted, data is grouped by date only.',
    ),
  metrics: z
    .array(z.string())
    .describe('The Requested metrics. Must be taken from the "Available metrics" list'),
  startDate: isoDateUtcSchema
    .describe('Requested start date (start of the day)')
    .nullable()
    .optional(),
  endDate: isoDateUtcSchema.describe('Requested end date (end of the day)').nullable().optional(),
  filter: z
    .object({
      dimension: z.string().describe('The filtering dimension'),
      values: z.array(z.string()),
    })
    .nullable()
    .optional(),
  eventType: z
    .nativeEnum(AiAnalyticsEventType)
    .default(AiAnalyticsEventType.Adserving)
    .describe('The event type'),
  type: z
    .nativeEnum(AiDashboardWidgetType)
    .describe('widget type')
    .default(AiDashboardWidgetType.LINE_AREA_CHART),
});

function accountAnalyticsDataTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolError('Campaign data is unavailable, please try again later');
  }

  async function execute({
    input: {
      dimension: aiDimension,
      metrics: aiMetrics,
      startDate,
      endDate,
      filter,
      eventType: aiEventType,
      type,
    },
    toolUtils,
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const {
      dashboardPage: {
        startDate: startDateFromStore,
        endDate: endDateFromStore,
        timezone,
        metricsAndDimensionsOptions,
        filters,
        timezoneOptions,
      },
    } = store.getState();

    const eventType = getMatchingEnumValue(
      AiAnalyticsEventType,
      AnalyticsEventType,
      aiEventType,
    ) as unknown as AnalyticsEventType;

    const timezoneObject = _find(timezoneOptions, {
      abbr: timezone,
    }) as TimezoneObject;
    const dateRange = {
      startDate: forceTimeZone(
        startDate ? DateTime.fromISO(startDate).toUTC() : DateTime.fromISO(startDateFromStore),
        0,
      ),

      endDate: forceTimeZone(
        endDate ? DateTime.fromISO(endDate).toUTC() : DateTime.fromISO(endDateFromStore),
        0,
      ).endOf('day'),
    };

    const metrics = _map(aiMetrics, (metric) =>
      getRemoteMetricKey(metricsAndDimensionsOptions.metrics, metric),
    ) as MetricValue[];

    const [dimensionsByEventType] = partitionByEventType(
      metricsAndDimensionsOptions.dimensions,
      eventType,
    ) as unknown as AvailableDimension[][];

    const dimension =
      getRemoteDimensionKey<DimensionValue>(dimensionsByEventType, aiDimension) ??
      DimensionValue.Date;

    const dimensions = [dimension];

    const filterKey = getRemoteDimensionKey<DimensionValue>(
      filters as unknown as AvailableDimension[],
      filter?.dimension,
    );

    const allFilters = [
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    if ([DimensionValue.Date, DimensionValue.Month, DimensionValue.Week].includes(dimension)) {
      function getGroupBy() {
        switch (dimension) {
          case DimensionValue.Month:
            return MetricsGroupBy.Month;
          case DimensionValue.Week:
            return MetricsGroupBy.Week;
          default:
            return MetricsGroupBy.Day;
        }
      }

      const analyticsData = await analyticsApi.getAnalyticsData({
        dimensions,
        metrics,
        dateRange,
        selectedTimezone: timezoneObject,
        groupBy: getGroupBy(),
        filters: allFilters,
        eventType,
      });

      if (!analyticsData.length) {
        return handleNoAnalyticsData();
      }

      const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

      if (!isSkillsMode) {
        return toolResult(JSON.stringify({analyticsData, dimensions}));
      }

      return persistInlineWidgetToAccountDashboard({
        toolUtils,
        callChain,
        title: 'Account Analytics Data',
        params: {
          metrics,
          analyticsData,
          dimensions,
          startDate: dateRange.startDate.toISO(),
          endDate: dateRange.endDate.toISO(),
          type,
          breakdownByTime: true,
        },
      });
    } else {
      const {analyticsData: topPerformingData, dimensionValues} =
        await analyticsApi.getDashboardWidgetData({
          dimensions,
          metrics,
          dateRange,
          selectedTimezone: timezoneObject,
          limit: 5,
          filters: allFilters,
          topPerforming: true,
          eventType,
        });

      const isUnique = isUniqueDimensionValue(dimension);
      const mainParameter = getDimensionMainParameter(isUnique, dimension);

      const dimensionFilter = {
        filterBy: mainParameter,
        allValues: _map(topPerformingData, (row) => row[mainParameter]),
      } as AnalyticsFilterItem;

      const dimensionFilterValues = _map(dimensionFilter.allValues, (value) => ({
        [mainParameter]: value,
      }));

      const analyticsData = await analyticsApi.getAnalyticsData({
        dimensions,
        metrics,
        dateRange,
        selectedTimezone: timezoneObject,
        groupBy: MetricsGroupBy.Day,
        filters: [...allFilters, dimensionFilter],
        eventType,
        limit: ANALYTICS_LIMIT,
      });

      if (!analyticsData.length) {
        return handleNoAnalyticsData();
      }

      const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

      if (!isSkillsMode) {
        return toolResult(
          JSON.stringify({
            analyticsData,
            dimensionValues,
            dimensionFilterValues,
            breakdownByTime: true,
          }),
        );
      }

      return persistInlineWidgetToAccountDashboard({
        toolUtils,
        callChain,
        title: 'Account Analytics Data',
        params: {
          metrics,
          analyticsData,
          dimensions,
          startDate: dateRange.startDate.toISO(),
          endDate: dateRange.endDate.toISO(),
          type,
          breakdownByTime: true,
          dimensionValues,
          dimensionFilterValues,
        },
      });
    }
  }

  return tool({
    name: AccountAnalyticsToolName.AccountAnalyticsData,
    displayName: 'Loading Analytics Data',
    description:
      'Retrieves campaign performance over time, optionally broken down by a specified dimension',
    execute,
    inputSchema,
  });
}

export default accountAnalyticsDataTool;
